/**
 * Request formatting: map user params onto an endpoint definition.
 * Port of fastSDK's `APIClient.format_request_params` + file handling,
 * collapsed to the Socaity/APIPod protocol:
 *
 * - `application/json` bodies: media values are serialized as FileModel JSON.
 * - `multipart/form-data` bodies: media values are uploaded as Blobs.
 * - `application/x-www-form-urlencoded`: scalar form fields (no files).
 *
 * Sole JSON object bodies (e.g. catalog chat param ``request`` wrapping
 * ChatCompletionRequest) are spread onto the wire root — FastAPI's
 * ``Body(ChatCompletionRequest)`` expects flat fields, not ``{request: …}``.
 *
 * Gateway management extras (`is_public`, `keep`, `is_temporary`) always go
 * on the query string (gate mounts them as Query), never in the JSON body.
 */

import { MediaFile, MediaFileFactory, isUrl, isValidFilePath } from '@socaity/media-toolkit';

import {
  EndpointDefinition,
  EndpointParameter,
  ServiceAddress,
  buildEndpointUrl,
  isFileParameter,
} from '../schemas/definitions';

export interface FormattedRequest {
  url: string;
  method: string;
  query: Record<string, any>;
  body: BodyInit | null;
  headers: Record<string, string>;
}

const JSON_CONTENT = 'application/json';
const FORM_CONTENT = 'application/x-www-form-urlencoded';

/** Gate mounts these as Query on every hosted route (see APIPodMaster builder). */
const GATEWAY_MANAGEMENT_PARAMS = new Set(['is_public', 'keep', 'is_temporary']);

export async function formatRequest(
  address: ServiceAddress,
  endpoint: EndpointDefinition,
  params: Record<string, any>,
): Promise<FormattedRequest> {
  const declared = new Map(endpoint.parameters.map((param) => [param.name, param]));
  const bodyParams = endpoint.parameters.filter((param) => param.location === 'body');
  const soleJsonObjectBody =
    endpoint.request_body_content_type === JSON_CONTENT
    && bodyParams.length === 1
    && isJsonObjectRequestBody(bodyParams[0]);

  const query: Record<string, any> = {};
  const bodyValues: Record<string, any> = {};
  const fileValues: Record<string, any> = {};

  // Declared params: defaults + required validation + query/body/file routing.
  for (const param of endpoint.parameters) {
    const value = params[param.name] ?? param.default;
    if (value === undefined || value === null) {
      if (param.required) throw new Error(`Required parameter '${param.name}' is missing`);
      continue;
    }
    if (GATEWAY_MANAGEMENT_PARAMS.has(param.name)) {
      query[param.name] = value;
      continue;
    }
    if (isFileParameter(param) || isMediaValue(value)) {
      fileValues[param.name] = value;
    } else if (param.location === 'query') {
      query[param.name] = value;
    } else if (
      soleJsonObjectBody
      && param.name === bodyParams[0].name
      && typeof value === 'object'
      && !Array.isArray(value)
    ) {
      // Spread ChatCompletionRequest (etc.) onto the JSON root.
      Object.assign(bodyValues, value);
    } else {
      bodyValues[param.name] = value;
    }
  }

  // Undeclared params: management → query; media → files; else body.
  for (const [name, value] of Object.entries(params)) {
    if (declared.has(name) || value === undefined || value === null) continue;
    if (GATEWAY_MANAGEMENT_PARAMS.has(name)) {
      query[name] = value;
      continue;
    }
    if (isMediaValue(value)) {
      fileValues[name] = value;
    } else {
      bodyValues[name] = value;
    }
  }

  const contentType = endpoint.request_body_content_type ?? inferContentType(fileValues);
  const url = buildEndpointUrl(address, endpoint.path);
  // Job submissions are always POST (mirrors fastSDK; catalog definitions
  // may carry an untrustworthy default method).
  const method = 'POST';

  if (contentType === JSON_CONTENT) {
    for (const [name, value] of Object.entries(fileValues)) {
      bodyValues[name] = await toJsonFileValue(value, declared.get(name));
    }
    return {
      url,
      method,
      query,
      body: JSON.stringify(bodyValues),
      headers: { 'Content-Type': JSON_CONTENT, Accept: 'application/json' },
    };
  }

  if (Object.keys(fileValues).length === 0 && contentType === FORM_CONTENT) {
    const form = new URLSearchParams();
    for (const [name, value] of Object.entries(bodyValues)) {
      form.append(name, encodeFormField(value));
    }
    return { url, method, query, body: form, headers: {} };
  }

  // multipart/form-data (fetch sets the boundary header itself).
  // Params declared as plain FileModel (no raw-upload variant) travel as
  // FileModel JSON form fields; everything else as raw multipart uploads.
  const formData = new FormData();
  for (const [name, value] of Object.entries(bodyValues)) {
    formData.append(name, encodeFormField(value));
  }
  for (const [name, value] of Object.entries(fileValues)) {
    const param = declared.get(name);
    if (param && isFileModelOnlyParameter(param)) {
      formData.append(name, encodeFormField(await toJsonFileValue(value, param)));
    } else {
      await appendFileField(formData, name, value);
    }
  }
  return { url, method, query, body: formData, headers: {} };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function inferContentType(fileValues: Record<string, any>): string {
  return Object.keys(fileValues).length ? 'multipart/form-data' : FORM_CONTENT;
}

function isJsonObjectRequestBody(param: EndpointParameter): boolean {
  const schema = param.param_schema ?? {};
  if (!schema || typeof schema !== 'object') return false;
  if (schema.type === 'object' && schema.properties) return true;
  return Boolean(schema.properties);
}

const RAW_UPLOAD_FORMATS = ['binary', 'image', 'video', 'audio'];

/**
 * True when the param expects an APIPod FileModel JSON payload and offers no
 * raw-upload variant (port of `_param_is_file_model` / `_param_accepts_raw_upload`).
 */
function isFileModelOnlyParameter(param: EndpointParameter): boolean {
  const definitions = param.definition == null
    ? []
    : Array.isArray(param.definition) ? param.definition : [param.definition];
  if (!definitions.length) return false;
  const formats = new Set(definitions.map((definition) => definition.format).filter(Boolean));
  if (!formats.has('file') || RAW_UPLOAD_FORMATS.some((format) => formats.has(format))) return false;
  return !schemaAcceptsBinaryUpload(param);
}

function schemaAcceptsBinaryUpload(param: EndpointParameter): boolean {
  const schema = param.param_schema ?? {};
  const options: any[] = schema.anyOf ?? schema.oneOf ?? [];
  return options.some(
    (option) => option?.type === 'string'
      && (option.format === 'binary' || option.contentMediaType === 'application/octet-stream'),
  );
}

function isMediaValue(value: any): boolean {
  if (value instanceof MediaFile) return true;
  if (typeof Blob !== 'undefined' && value instanceof Blob) return true;
  if (Array.isArray(value)) return value.some(isMediaValue);
  return false;
}

function encodeFormField(value: any): string {
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/** Load a file param value into a MediaFile; URL strings pass through untouched. */
async function loadMedia(value: any): Promise<MediaFile | string> {
  if (value instanceof MediaFile) return value;
  if (typeof value === 'string') {
    // URLs are sent as-is: the service downloads them (FileModel accepts URI content).
    if (isUrl(value)) return value;
    if (await isValidFilePath(value)) return MediaFileFactory.create(value);
    return value; // base64 or plain string payload
  }
  return MediaFileFactory.create(value);
}

async function toJsonFileValue(value: any, param?: EndpointParameter): Promise<any> {
  if (Array.isArray(value)) {
    return Promise.all(value.map((item) => toJsonFileValue(item, param)));
  }
  const media = await loadMedia(value);
  return media instanceof MediaFile ? media.toJson() : media;
}

async function appendFileField(formData: FormData, name: string, value: any): Promise<void> {
  if (Array.isArray(value)) {
    for (const item of value) await appendFileField(formData, name, item);
    return;
  }
  const media = await loadMedia(value);
  if (media instanceof MediaFile) {
    const info = media.getInfo();
    formData.append(name, media.toBlob(), info.fileName);
  } else {
    formData.append(name, media);
  }
}
